import {HttpHeaders} from '@angular/common/http';
import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {DcpSetupIntent} from '@app/model/stripe/setup-intent';
import {DpaoService} from '@app/services/dpao/dpao.service';
import {DcpApiService, FeatureTogglesService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {SetupIntent} from '@stripe/stripe-js';
import {Observable, of} from 'rxjs';
import {first, map, switchMap} from 'rxjs/operators';
import {DcpCcPspService, PaymentProvider} from '../../services/dcp-cc-psp.service';
import {DcpStripeService} from '../../services/stripe/dcp-stripe.service';

@UntilDestroy()
@Component({
  selector: 'dcp-mock-add-new-cc',
  templateUrl: './mock-add-new-cc.component.html',
  styleUrls: ['./mock-add-new-cc.component.scss']
})
export class MockAddNewCcComponent implements OnInit {

  @Input() isClosable: boolean = false;
  isOpened: boolean = false;
  showErrorMessages: boolean;

  private mockApiAuth: string = 'Bearer sk_test_mock';
  private registerCreditCardInDpaoFeatureToggle = false;

  form: FormGroup = this.formBuilder.group({
    cardNumber: this.formBuilder.control(
      {
        value: '',
        disabled: false
      },
      {
        validators: [
          Validators.required,
          Validators.minLength(16),
          Validators.maxLength(19),
        ]
      }
    ),
    expiration: this.formBuilder.control(
      {
        value: '',
        disabled: false
      },
      {
        validators: [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(5),
        ]
      }
    ),
    cvv: this.formBuilder.control(
      {
        value: '',
        disabled: false
      },
      {
        validators: [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(4),
        ]
      }
    )
  }, {
    validators: []
  });

  constructor(private formBuilder: FormBuilder,
              private dcpStripeService: DcpStripeService,
              private dcpCcPspService: DcpCcPspService,
              private dcpApiService: DcpApiService,
              private dpaoService: DpaoService,
              private featureTogglesService: FeatureTogglesService,
              private userAccountFacade: UserAccountFacade) {
  }

  ngOnInit(): void {
    this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.registerCreditCardInDPAO).pipe(untilDestroyed(this)).subscribe(enabled => {
      this.registerCreditCardInDpaoFeatureToggle = enabled;
    });

    this.dcpCcPspService.isAddNewCreditCardComponentOpened().pipe(
      untilDestroyed(this)
    ).subscribe(isOpened => {
      this.isOpened = isOpened;
    });
  }

  onSubmit(): void {
    if (!this.form.invalid) {
      this.dcpCcPspService.getCurrentCartPaymentProviderId().pipe(
        untilDestroyed(this),
        first()
      ).subscribe((paymentProviderId: string) => {
        switch (paymentProviderId) {
          case PaymentProvider.MOCK_STRIPE:
            this.saveStripeCc();
            break;
          default:
            console.error('Expected mock payment provider, but received: ', paymentProviderId);
        }
      });
    } else {
      this.showErrorMessages = true;
    }
  }

  onCancel() {
    this.form.reset();
    this.showErrorMessages = false;
    this.dcpCcPspService.closeAddNewCreditCardComponent();
  }

  private saveStripeCc(): void {
    const headers: HttpHeaders = new HttpHeaders({
      Authorization: this.mockApiAuth,
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    const body: URLSearchParams = new URLSearchParams();
    body.set('expand[0]', 'payment_method');
    body.set('payment_method_data[type]', 'us_bank_account');
    let currentUser: User;

    this.userAccountFacade.get()
      .pipe(
        untilDestroyed(this),
        first(),
        switchMap(user => {
          currentUser = user;
          return this.dcpApiService.callKyma<SetupIntent>('mockStripeApi', 'setupIntents', {
            secret: 'mock_secret'
          }, {headers, body}, 'POST');
        }),
        switchMap(setupIntentWithPaymentMethod => this.registerCreditCardInDpao(setupIntentWithPaymentMethod))
      ).subscribe((setupIntent) => {
        this.dcpStripeService.saveCreditCardDetails(currentUser, setupIntent).pipe(untilDestroyed(this)).subscribe(() => {
          this.onCancel();
        });
      }
    );
  }

  private registerCreditCardInDpao(setupIntent: DcpSetupIntent): Observable<DcpSetupIntent> {
    if (this.registerCreditCardInDpaoFeatureToggle) {
      return this.dpaoService.registerCreditCard(setupIntent).pipe(
        map(dpaoSubscriptionId => {
          if (dpaoSubscriptionId) {
            setupIntent.dpaoSubscriptionId = dpaoSubscriptionId;
          }
          return setupIntent;
        })
      );
    } else {
      return of(setupIntent);
    }
  }
}
